import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Space, Card } from "antd";
import { Storage } from "aws-amplify";
import { fileSizeInKB, fileSizeInMB, formatDate } from "../utils";
import DeleteAdUnitButton from "./DeleteAdUnitButton";

const MediaDetailsCard = ({
  orgMedia,
  assignedAdGroupUnit,
  selectedAdUnits = [],
  handleDeleteMedias = () => {},
  isVideo = false,
}) => {
  const [duration, setDuration] = useState(null);

  // Early return guard - moved after hooks
  const hasValidMedia = orgMedia && orgMedia.media;
  const mediaKey = hasValidMedia ? orgMedia.media.key : null;

  // Detect video duration for video files
  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const detectVideoDuration = async () => {
      if (!isVideo || !hasValidMedia || !mediaKey) return;

      try {
        const videoUrl = await Storage.get(mediaKey, { expires: 600000 });
        const videoDuration = await new Promise((resolve) => {
          const video = document.createElement('video');
          video.preload = 'metadata';

          const cleanup = () => {
            video.onloadedmetadata = null;
            video.onerror = null;
            video.src = '';
            video.load(); // Clear video resources
          };

          video.onloadedmetadata = () => {
            const duration = Math.round(video.duration);
            cleanup();
            resolve(duration);
          };

          video.onerror = () => {
            cleanup();
            resolve(null);
          };

          video.src = videoUrl;
        });

        // Only update state if component is still mounted
        if (isMounted && videoDuration) {
          setDuration(videoDuration);
        }
      } catch (error) {
        console.warn('Failed to detect video duration:', error);
      }
    };

    detectVideoDuration();

    // Cleanup function to prevent setState on unmounted component
    return () => {
      isMounted = false;
    };
  }, [isVideo, hasValidMedia, mediaKey]);

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Early return after all hooks
  if (!hasValidMedia) return null;

  const { media } = orgMedia;

  // Dynamic content based on media type
  const title = isVideo ? "VIDEO UNIT DETAILS" : "AD UNIT DETAILS";
  const fileSizeFormatter = isVideo ? fileSizeInMB : fileSizeInKB;
  const fileSizeUnit = isVideo ? "MB" : "KB";

  return (
    <Card
      title={title}
      extra={
        <DeleteAdUnitButton
          onDelete={() =>
            handleDeleteMedias(
              orgMedia && orgMedia.media ? [orgMedia.media.id] : []
            )
          }
        />
      }
      className="ccard-detail"
    >
      <Space direction="vertical" style={{ width: "100%" }} size={0}>
        <Row>
          <Col span={4}>
            <Typography.Text light>Aspect</Typography.Text>
          </Col>
          {isVideo && (
            <Col span={4}>
              <Typography.Text light>Duration</Typography.Text>
            </Col>
          )}
          <Col span={4}>
            <Typography.Text light>File Size</Typography.Text>
          </Col>
          <Col span={4}>
            <Typography.Text light>File Type</Typography.Text>
          </Col>
          <Col span={5}>
            <Typography.Text light>Assigned Ad Groups</Typography.Text>
          </Col>
        </Row>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <Row>
            <Col span={4}>
              <Typography.Text>{`${media.width}x${media.height}`}</Typography.Text>
            </Col>
            {isVideo && (
              <Col span={4}>
                <Typography.Text>{formatDuration(duration)}</Typography.Text>
              </Col>
            )}
            <Col span={4}>
              <Typography.Text>{fileSizeFormatter(media.size)} {fileSizeUnit}</Typography.Text>
            </Col>
            <Col span={4}>
              <Typography.Text>{media.fileType}</Typography.Text>
            </Col>
            <Col span={5}>
              <Typography.Text strong>
                {assignedAdGroupUnit && assignedAdGroupUnit.length}
              </Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Typography.Text strong>Created On</Typography.Text>
            </Col>
          </Row>
        </Space>
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <Row>
            <Col span={24}>
              <Typography.Text>{formatDate(media.createdAt)}</Typography.Text>
            </Col>
          </Row>
          {/* <Row>
            <Col span={6}>
              <Typography.Text strong>Channel Partner</Typography.Text>
            </Col>
            <Col span={18}>
              <Typography.Text strong>Advertiser</Typography.Text>
            </Col>
          </Row> */}
        </Space>
        {/* <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <Row>
            <Col span={6}>
              <Typography.Text>TO DO</Typography.Text>
            </Col>
            <Col span={18}>
              <Typography.Text>TO DO</Typography.Text>
            </Col>
          </Row>
        </Space> */}
      </Space>
    </Card>
  );
};

export default MediaDetailsCard;